import React, { useEffect, useRef, useState } from "react";
import { Image, Stack } from "@fluentui/react";
import "./App.css";
import html2canvas from "html2canvas";
import Header from "./Header";
import Wheel from "./Wheel";
import Input from "./Input";
import Footer from "./Footer";
import Snow from "./Snow";
import Rehnis from "./Rehnis";
import ThemeSwitcher from "./ThemeSwitcher";

export type AppTheme = "fikahjulet" | "tomtbola" | "bw";
export type AppView = "input" | "wheel";

// Namn lagras som en kommaseparerad lista i ?default=.
// URLSearchParams sköter all procentkodning - koda aldrig namnen manuellt,
// det ger dubbelkodning (Måns -> M%25C3%25A5ns).
const parseNames = (param: string | null): string[] => {
  if (!param) return [];
  return param
    .split(",")
    .map(n => {
      // Tolerera gamla, dubbelkodade länkar: %C3%A5 ligger kvar efter att
      // URLSearchParams redan avkodat ett lager.
      if (/%[0-9A-Fa-f]{2}/.test(n)) {
        try {
          return decodeURIComponent(n);
        } catch {
          return n;
        }
      }
      return n;
    })
    .map(n => n.trim())
    .filter(n => n !== "");
};

const buildUrlParams = (
  names: string[],
  theme: AppTheme,
  trioMode: boolean
): string => {
  const params = new URLSearchParams(window.location.search);
  params.set("theme", theme);
  params.set("trio", trioMode ? "true" : "false");
  params.delete("names");

  const validNames = names.filter(name => name.trim() !== "");
  if (validNames.length > 0) {
    params.set("default", validNames.join(","));
  } else {
    params.delete("default");
  }

  // Komma är tillåtet i en query string - håll länken läsbar.
  return params.toString().replace(/%2C/g, ",");
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<AppTheme>("fikahjulet");
  const [currentView, setCurrentView] = useState<AppView>("input");
  const [trioMode, setTrioMode] = useState<boolean>(false);

  const [innerRotation, setInnerRotation] = useState<number>(0);
  const [outerRotation, setOuterRotation] = useState<number>(0);
  const [thirdRotation, setThirdRotation] = useState<number>(0);

  const [defaultNames, setDefaultNames] = useState<string[]>([]);
  const [allNames, setAllNames] = useState<string[]>([]);
  const [innerNames, setInnerNames] = useState<string[]>([]);
  const [outerNames, setOuterNames] = useState<string[]>([]);
  const [thirdNames, setThirdNames] = useState<string[]>([]);

  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const captureRef = useRef<HTMLDivElement>(null);

  const [showCopyTooltip, setShowCopyTooltip] = useState<boolean>(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const nameParam = queryParams.get("names");
    const defaultParam = queryParams.get("default");
    const defaultTheme = queryParams.get("theme") as AppTheme | null;
    const savedTheme = localStorage.getItem("theme") as AppTheme | null;
    const trioParam = queryParams.get("trio");

    // Set trio mode from URL parameter
    if (trioParam === "true") {
      setTrioMode(true);
    } else if (trioParam === "false") {
      setTrioMode(false);
    }

    const initialTheme = defaultTheme || savedTheme || "fikahjulet";
    if (["fikahjulet", "tomtbola", "bw"].includes(initialTheme)) {
      setTheme(initialTheme);
    } else {
      setTheme("fikahjulet");
    }

    // Load names from URL parameters only - no localStorage
    const urlNames = nameParam ? parseNames(nameParam) : parseNames(defaultParam);
    
    setAllNames(urlNames);
    setDefaultNames(urlNames); // For Input component prefill

    // Always start in input view, wheel view is triggered by user action
    setCurrentView("input");
  }, []);

  useEffect(() => {
    // Update names when all names change or trio mode changes
    if (trioMode) {
      const [inner, middle, outer] = splitArrayIntoThree(allNames);
      setInnerNames(inner);
      setOuterNames(middle);
      setThirdNames(outer);
    } else {
      const [inner, outer] = splitArrayRandomly(allNames);
      setInnerNames(inner);
      setOuterNames(outer);
      setThirdNames([]); // Clear third ring names when not in trio mode
    }
  }, [allNames, trioMode]);

  const setNewTheme = (newTheme: AppTheme) => {
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  //split the array in three random parts for trio mode
  function splitArrayIntoThree(arr: string[]): [string[], string[], string[]] {
    if (arr.length === 0) {
      return [[], [], []];
    }

    // Shuffle the array to randomize trios
    const shuffledArr = [...arr].sort(() => Math.random() - 0.5);
    
    // Calculate minimum segments needed for ring appearance
    const minSegmentsPerRing = Math.max(3, Math.ceil(arr.length / 3));
    
    // Divide names between three rings
    let innerRing: string[] = [];
    let middleRing: string[] = [];
    let outerRing: string[] = [];
    
    // Distribute names evenly between the three rings
    for (let i = 0; i < shuffledArr.length; i++) {
      if (i % 3 === 0) {
        // First group goes to inner ring
        innerRing.push(shuffledArr[i]);
      } else if (i % 3 === 1) {
        // Second group goes to middle ring
        middleRing.push(shuffledArr[i]);
      } else {
        // Third group goes to outer ring
        outerRing.push(shuffledArr[i]);
      }
    }
    
    // Handle case when names don't distribute evenly
    // If there are remainders, add placeholders to make rings have equal segments
    while (innerRing.length < minSegmentsPerRing) {
      innerRing.push("");
    }
    
    while (middleRing.length < minSegmentsPerRing) {
      middleRing.push("");
    }
    
    while (outerRing.length < minSegmentsPerRing) {
      outerRing.push("");
    }
    
    return [innerRing, middleRing, outerRing];
  }

  //split the array in two random parts and make them the same length
  function splitArrayRandomly(arr: string[]): [string[], string[]] {
    if (arr.length === 0) {
      return [[], []];
    }

    // Shuffle the array to randomize pairings
    const shuffledArr = [...arr].sort(() => Math.random() - 0.5);
    
    // Calculate segments needed for each ring - minimum 2 segments per ring
    const minSegmentsPerRing = Math.max(2, Math.ceil(arr.length / 2));
    
    // Divide names between rings - inner and outer
    let innerRing: string[] = [];
    let outerRing: string[] = [];
    
    // Distribute names evenly
    for (let i = 0; i < shuffledArr.length; i++) {
      if (i % 2 === 0) {
        // Even indices go to inner ring
        innerRing.push(shuffledArr[i]);
      } else {
        // Odd indices go to outer ring
        outerRing.push(shuffledArr[i]);
      }
    }
    
    // If odd number of names, add a single dot to the smaller ring
    if (arr.length % 2 === 1) {
      outerRing.push(".");
    }
    
    // Ensure both rings have at least minSegmentsPerRing segments
    // This is important for wheel to look good with few names
    while (innerRing.length < minSegmentsPerRing) {
      innerRing.push("");  // Using empty string instead of dots for invisible segments
    }
    
    while (outerRing.length < minSegmentsPerRing) {
      outerRing.push("");  // Using empty string instead of dots for invisible segments
    }
    
    return [innerRing, outerRing];
  }

  const handleCapture = () => {
    if (captureRef.current) {
      setIsCapturing(true);
      setTimeout(() => {
        html2canvas(captureRef.current!).then(function (canvas) {
          const imgData = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          link.href = imgData;
          link.download = "fikahjul.png";
          link.click();
        });
        setIsCapturing(false);
      }, 10);
    }
  };

  const handleSegmentClick = () => {
    // Calculate random rotation, but ensure rings align after spinning
    // All rings must rotate by multiples of the same angle to ensure proper alignment
    const segmentAngle = 360 / innerNames.length;
    
    // Always do a full reset - more dramatic spin each time
    // First reset to 0 to ensure we always start from a clean state
    setInnerRotation(0);
    setOuterRotation(0);
    setThirdRotation(0);
    
    // Small timeout to ensure the reset is applied before the new rotation
    setTimeout(() => {
      // Calculate random number of segments to rotate by - at least 2 full rotations
      const minRotations = 2; // Minimum number of complete rotations
      const maxRotations = 4; // Maximum number of complete rotations for variety
      
      // Get a random number of rotations between min and max
      const randomRotations = minRotations + Math.random() * (maxRotations - minRotations);
      const randomSegments = Math.floor(Math.random() * innerNames.length);
      
      // Calculate total segments to rotate
      const rotateSegments = Math.floor(randomRotations * innerNames.length) + randomSegments;
      
      // Apply the rotations
      const newInnerRotation = -(rotateSegments * segmentAngle);
      const newOuterRotation = rotateSegments * segmentAngle;
      const newThirdRotation = -(rotateSegments * segmentAngle); // Third ring rotates opposite to middle
      
      setInnerRotation(newInnerRotation);
      setOuterRotation(newOuterRotation);
      setThirdRotation(newThirdRotation);
    }, 50); // Short delay to ensure state update cycle completes
  };

  const handleAdjustNames = () => {
    setCurrentView("input");
  };

  // Function to copy the current URL with theme and names
  const copyUrl = () => {
    const urlToCopy = `${window.location.origin}${window.location.pathname}?${buildUrlParams(allNames, theme, trioMode)}`;

    navigator.clipboard.writeText(urlToCopy).then(() => {
      setShowCopyTooltip(true);
      setTimeout(() => {
        setShowCopyTooltip(false);
      }, 1500);
    }).catch(err => {
      console.error('Failed to copy link: ', err);
    });
  };

  const saveBackground: React.CSSProperties = isCapturing
    ? { backgroundColor: theme === "tomtbola" ? "#d9d9d9" : "#251b30" }
    : {};

  // Function to handle switching to wheel view and setting names
  const showWheelView = (finalNames: string[]) => {
    // First set all names
    setAllNames(finalNames);
    
    // Update URL with current names (without relying on localStorage)
    const newUrl = `${window.location.pathname}?${buildUrlParams(finalNames, theme, trioMode)}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
    
    // Reset rotations to ensure proper starting position
    setInnerRotation(0);
    setOuterRotation(0);
    setThirdRotation(0);
    
    // Force immediate recalculation of names based on current mode
    if (trioMode) {
      const [newInnerNames, newMiddleNames, newOuterNames] = splitArrayIntoThree(finalNames);
      setInnerNames(newInnerNames);
      setOuterNames(newMiddleNames);
      setThirdNames(newOuterNames);
    } else {
      const [newInnerNames, newOuterNames] = splitArrayRandomly(finalNames);
      setInnerNames(newInnerNames);
      setOuterNames(newOuterNames);
      setThirdNames([]); // Clear third ring names
    }
    
    // Finally switch the view
    setCurrentView("wheel");
  };

  // Function to validate names and show the wheel
  const handleShowWheelClick = () => {
    const finalValidNames = allNames.filter((name) => name.trim() !== "");
    if (finalValidNames.length < 3) {
      // Maybe add a temporary message/alert here?
      console.log("Need at least 3 names to show the wheel.");
      // Optionally switch back to input view if not already there
      if (currentView !== 'input') {
        setCurrentView('input');
      }
      return; // Prevent showing the wheel
    }
    // If validation passes, show the wheel
    showWheelView(finalValidNames); 
  };

  // Toggle trio mode
  const toggleTrioMode = () => {
    setTrioMode(!trioMode);
    
    // Reset rotations when changing modes
    setInnerRotation(0);
    setOuterRotation(0);
    setThirdRotation(0);
  };

  let mainContent: JSX.Element = <></>;

  if (currentView === "input") {
    // Input View
    mainContent = (
      <div className="SaveToPng input-view-container" ref={captureRef}>
        <Stack verticalAlign="center" tokens={{ childrenGap: "1em" }}>
          {allNames.length > 0 && allNames.length < 3 && (
            <Stack.Item align="center">
              <h2>Måste ha minst tre namn!</h2>
            </Stack.Item>
          )}
          <Stack.Item align="center">
            <Input 
              theme={theme} 
              currentNames={allNames}
              onShowWheel={showWheelView}
              trioMode={trioMode}
              onToggleTrioMode={toggleTrioMode}
            />
          </Stack.Item>
        </Stack>
      </div>
    );
  } else if (currentView === "wheel") {
    // Wheel View
    mainContent = (
      <>
        <div className="SaveToPng wheel-view-container" ref={captureRef} style={saveBackground}>
          <Stack verticalAlign="center" tokens={{ childrenGap: "0" }}>
            <Stack.Item align="center">
              <Wheel
                theme={theme}
                onClick={handleSegmentClick}
                innerNames={innerNames}
                outerNames={outerNames}
                thirdNames={thirdNames}
                innerRotation={innerRotation}
                outerRotation={outerRotation}
                thirdRotation={thirdRotation}
                trioMode={trioMode}
              />
            </Stack.Item>
          </Stack>
        </div>
        {/* Button container for wheel view */}
        <div className="wheel-button-container">
          {/* Save Image Button */}
          <button 
            className={`Button ${theme}-button save-image-button`} 
            onClick={handleCapture}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {/* Download Icon SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="currentColor" style={{ marginRight: '0.5rem' }} ><path d="M0 0h24v24H0V0z" fill="none"/><path d="M19 9h-4V3H9v6H5l7 7 7-7zm-8 2V5h2v6h1.17L12 13.17 9.83 11H11zm-6 7h14v2H5z"/></svg>
            Spara som bild
          </button>


          {/* Adjust Names Button */}
          <button 
            className={`Button ${theme}-button adjust-names-button`} 
            onClick={() => setCurrentView("input")} 
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {/* Edit Icon SVG */}
            <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="currentColor" style={{ marginRight: '0.5rem' }}><path d="M0 0h24v24H0V0z" fill="none"/><path d="M14.06 9.02l.94.94L5.92 19H5v-.92l9.06-9.06M17.66 3c-.26 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/></svg>
            Ändra inställningar
          </button>
        </div>
      </>
    );
  }

  return (
    <div className={`App ${theme}`}>
      <Header theme={theme} onLogoClick={handleAdjustNames} />
      <ThemeSwitcher 
        currentTheme={theme} 
        currentView={currentView}
        setTheme={setNewTheme} 
        onCapture={handleCapture} 
        onAdjust={handleAdjustNames} 
        onShowWheelClick={handleShowWheelClick}
        onCopyLink={copyUrl} 
        showCopyTooltip={showCopyTooltip}
      />

      <div className="main-content-area">
        {mainContent}
      </div>

      {theme === "tomtbola" && (
        <Image
          className="floating-image"
          src="./tomtbola.svg"
          styles={{ image: { width: "40vw", maxWidth: "400px" } }}
        />
      )}

      <div className="Logo">
        <Footer theme={theme} setTheme={setNewTheme} />
      </div>

      {theme === "tomtbola" && (
        <Rehnis theme={theme} isInput={allNames.length < 3} />
      )}
    </div>
  );
};

export default App;